import React, { useState } from 'react';
import { 
  Select, 
  MenuItem, 
  FormControl, 
  Box, 
  Typography,
  Fade,
  Popper,
  Paper,
  ClickAwayListener
} from '@mui/material';
import { LanguageOutlined } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';

const LanguageSelector = ({ variant = 'select' }) => {
  const { currentLanguage, languages, changeLanguage } = useLanguage();
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setOpen(false);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(!open);
  };

  const handleClickAway = () => {
    setOpen(false);
  };

  // Dropdown variant (default)
  if (variant === 'dropdown') {
    return (
      <ClickAwayListener onClickAway={handleClickAway}>
        <Box sx={{ position: 'relative' }}>
          <Box
            onClick={handleClick}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              padding: '8px 12px',
              borderRadius: 1,
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              '&:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          >
            <LanguageOutlined fontSize="small" />
            <Typography variant="body2">
              {currentLanguage.flag} {currentLanguage.nativeName}
            </Typography>
          </Box>
          
          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            transition
            sx={{ zIndex: 1300 }}
          >
            {({ TransitionProps }) => (
              <Fade {...TransitionProps} timeout={200}>
                <Paper 
                  sx={{ 
                    mt: 1, 
                    minWidth: 150,
                    boxShadow: 3,
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {languages.map((language) => (
                    <Box
                      key={language.code}
                      onClick={() => handleLanguageChange(language.code)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        padding: '12px 16px',
                        cursor: 'pointer',
                        backgroundColor: currentLanguage.code === language.code ? 'action.selected' : 'transparent',
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                    >
                      <Typography variant="body2">
                        {language.flag} {language.nativeName}
                      </Typography>
                    </Box>
                  ))}
                </Paper>
              </Fade>
            )}
          </Popper>
        </Box>
      </ClickAwayListener>
    );
  }

  // Select variant (Material-UI Select component)
  return (
    <FormControl size="small" sx={{ minWidth: 140 }}>
      <Select
        value={currentLanguage.code}
        onChange={(e) => handleLanguageChange(e.target.value)}
        displayEmpty
        sx={{
          '& .MuiSelect-select': {
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }
        }}
        startAdornment={<LanguageOutlined fontSize="small" sx={{ mr: 1 }} />}
      >
        {languages.map((language) => (
          <MenuItem key={language.code} value={language.code}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                {language.flag} {language.nativeName}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;